import type { FC } from 'react';
import { useEffect } from 'react';

import { Navigate, Outlet, useLocation } from 'react-router';

import { useLoginTrip } from '@/features/auth/api/useLoginTrip';
import { useInitialLoadGate } from '@/providers/InitialLoadGate';
import { storage, StorageKeys } from '@/services/storage';
import { useStorageValue } from '@/services/storage/react';

/**
 * Authentication guard layout that protects routes requiring user authentication.
 *
 * - Checks for GX token presence and redirects to login if missing
 * - Initializes Trip token authentication on mount
 * - Saves current pathname before redirect for post-login navigation
 * - Blocks child render until trip token, user config, markets, and dashboards are loaded
 *
 * ExportProvider and SymbolSelector are provided by PublicLayout.
 */
export const AuthLayout: FC = () => {
  const gxToken = useStorageValue(StorageKeys.GxToken);
  const { mutate: loginTrip } = useLoginTrip();
  const location = useLocation();

  useEffect(() => {
    if (gxToken) {
      loginTrip(gxToken);
    }
  }, [gxToken, loginTrip]);

  useEffect(() => {
    if (gxToken) return;

    storage.set(StorageKeys.LastPathname, location.pathname + location.search);
  }, [gxToken, location.pathname, location.search]);

  const isLoading = useInitialLoadGate(!!gxToken);

  if (!gxToken) {
    return (
      <Navigate
        replace
        to='/login'
      />
    );
  }

  if (isLoading) {
    return null;
  }

  return <Outlet />;
};
